import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { FilterState, StatFilter, StatFilterGroup } from '../../models/filter';
import { RootState } from '../../stores/store';
import { D2RSService } from '../../services/d2rsService';

// Helper to migrate old filter format to new format
const migrateOldFilters = (oldFilters: any): FilterState => {
  // If we detect old format (has addRarityFilterID), convert to new format
  if ('addRarityFilterID' in oldFilters) {
    // Map old rarity IDs to new string values
    const rarityMap: Record<
      string,
      '' | 'set' | 'white' | 'magic' | 'rare' | 'unique' | 'runeword' | 'misc' | 'gem'
    > = {
      '0': '',
      '1': 'unique',
      '2': 'set',
      '3': 'rare',
      '4': 'magic',
      '5': 'white',
      '6': 'runeword',
      '7': 'misc',
      '8': 'gem',
    };

    return {
      rarity:
        oldFilters.addRarityFilterID === '1'
          ? 'unique'
          : rarityMap[oldFilters.addRarityFilterID] || '',
      type: oldFilters.addTypeFilterID === '0' ? '' : oldFilters.addTypeFilterID || '',
      itemType: oldFilters.addItemTypeFilterID === '0' ? '' : oldFilters.addItemTypeFilterID || '',
      newItems: oldFilters.addNewItemsFilterID === '1' ? 'new-items-only' : '',
      ethereal:
        oldFilters.addEthItemsFilterID === '1'
          ? 'eth'
          : oldFilters.addEthItemsFilterID === '2'
            ? 'non-eth'
            : '',
      unidentified: oldFilters.unidFilterID === '1' ? 'unid' : '',
      limitFilter: oldFilters.limitFilter || 100,
      viewType: oldFilters.viewType || 'all',
      priceFilter: oldFilters.priceFilter || 'all',
      excludeStatFilters: oldFilters.statFilters || [],
      statFilterGroups: oldFilters.statFilterGroups || [],
      isFilterBubbleOpen: false,
      savedSearches: oldFilters.savedSearches || [],
      savedSearchSent: false,
      searchTerm: oldFilters.searchTerm || '',
      shouldApplySearch: false,
    };
  }

  // Make sure shouldApplySearch is included for existing filters
  if (oldFilters && !('shouldApplySearch' in oldFilters)) {
    return {
      ...oldFilters,
      shouldApplySearch: false,
    };
  }

  return oldFilters as FilterState;
};

// Selector to derive search term from current filters
export const selectDerivedSearchTerm = (state: RootState): string => {
  return D2RSService.convertFiltersToSearchString(state.filters);
};

// Helper to load filters from localStorage
const loadFiltersFromStorage = (): FilterState => {
  try {
    const storedFilters = localStorage.getItem('d2rs_filters');
    if (storedFilters) {
      const parsedFilters = JSON.parse(storedFilters);
      // Apply migration if needed
      return migrateOldFilters(parsedFilters);
    }
  } catch (error) {
    console.error('Error loading filters from localStorage:', error);
  }
  return initialState;
};

// Initial state
const initialState: FilterState = {
  rarity: '',
  type: '',
  itemType: '',
  newItems: '',
  ethereal: '',
  unidentified: '',
  limitFilter: 100,
  viewType: 'all',
  priceFilter: 'all',
  excludeStatFilters: [],
  statFilterGroups: [],
  isFilterBubbleOpen: false,
  savedSearches: [],
  savedSearchSent: false,
  searchTerm: '',
  shouldApplySearch: false,
};

// Helper to save filters to localStorage
const saveFiltersToStorage = (state: FilterState) => {
  try {
    const stateToSave = {
      ...state,
      // Don't persist these UI-specific properties
      isFilterBubbleOpen: initialState.isFilterBubbleOpen,
      savedSearchSent: initialState.savedSearchSent,
    };
    localStorage.setItem('d2rs_filters', JSON.stringify(stateToSave));
  } catch (error) {
    console.error('Error saving filters to localStorage:', error);
  }
};

const filterSlice = createSlice({
  name: 'filter',
  initialState: loadFiltersFromStorage(),
  reducers: {
    setRarity: (
      state,
      action: PayloadAction<
        '' | 'set' | 'white' | 'magic' | 'rare' | 'unique' | 'runeword' | 'misc' | 'gem'
      >
    ) => {
      state.rarity = action.payload;
      saveFiltersToStorage(state);
    },
    setType: (state, action: PayloadAction<string>) => {
      state.type = action.payload;
      saveFiltersToStorage(state);
    },
    setItemType: (state, action: PayloadAction<string>) => {
      state.itemType = action.payload;
      saveFiltersToStorage(state);
    },
    setNewItems: (state, action: PayloadAction<'' | 'new-items-only' | 'no-new-items'>) => {
      state.newItems = action.payload;
      saveFiltersToStorage(state);
    },
    setEthereal: (state, action: PayloadAction<'' | 'eth' | 'non-eth'>) => {
      state.ethereal = action.payload;
      saveFiltersToStorage(state);
    },
    setUnidentified: (state, action: PayloadAction<'' | 'unid' | 'non-unid'>) => {
      state.unidentified = action.payload;
      saveFiltersToStorage(state);
    },
    setLimitFilter: (state, action: PayloadAction<number>) => {
      state.limitFilter = action.payload;
      saveFiltersToStorage(state);
    },
    // Translation keys used dynamically: search.viewType.all, search.viewType.instant, search.viewType.products
    setViewType: (state, action: PayloadAction<'all' | 'instant' | 'products'>) => {
      state.viewType = action.payload;
      saveFiltersToStorage(state);
    },
    // Translation keys used dynamically: search.priceFilter.all, search.priceFilter.priced, search.priceFilter.unpriced
    setPriceFilter: (state, action: PayloadAction<'all' | 'priced' | 'unpriced'>) => {
      state.priceFilter = action.payload;
      saveFiltersToStorage(state);
    },
    toggleFilterBubble: (state) => {
      state.isFilterBubbleOpen = !state.isFilterBubbleOpen;
      // No need to save UI state to storage
    },
    setSavedSearches: (state, action: PayloadAction<any[]>) => {
      state.savedSearches = action.payload;
      saveFiltersToStorage(state);
    },
    setSavedSearchSent: (state, action: PayloadAction<boolean>) => {
      state.savedSearchSent = action.payload;
      // No need to save this temporary UI state
    },
    setSearchTerm: (state, action: PayloadAction<string>) => {
      state.searchTerm = action.payload;
      saveFiltersToStorage(state);
    },
    // Stat filter reducers
    addStatFilter: (state, action: PayloadAction<StatFilter>) => {
      state.excludeStatFilters.push(action.payload);
      saveFiltersToStorage(state);
    },
    removeStatFilter: (state, action: PayloadAction<string>) => {
      state.excludeStatFilters = state.excludeStatFilters.filter(
        (filter) => filter.id !== action.payload
      );
      saveFiltersToStorage(state);
    },
    updateStatFilter: (state, action: PayloadAction<StatFilter>) => {
      const index = state.excludeStatFilters.findIndex((filter) => filter.id === action.payload.id);
      if (index !== -1) {
        state.excludeStatFilters[index] = action.payload;
        saveFiltersToStorage(state);
      }
    },
    clearStatFilters: (state) => {
      state.excludeStatFilters = [];
      saveFiltersToStorage(state);
    },

    // New stat filter group reducers
    setStatFilterGroups: (state, action: PayloadAction<StatFilterGroup[]>) => {
      state.statFilterGroups = action.payload;
      saveFiltersToStorage(state);
    },
    addStatFilterGroup: (state, action: PayloadAction<StatFilterGroup>) => {
      state.statFilterGroups.push(action.payload);
      saveFiltersToStorage(state);
    },
    removeStatFilterGroup: (state, action: PayloadAction<string>) => {
      state.statFilterGroups = state.statFilterGroups.filter(
        (group) => group.id !== action.payload
      );
      saveFiltersToStorage(state);
    },
    addFilterToGroup: (state, action: PayloadAction<{ groupId: string; filter: StatFilter }>) => {
      const { groupId, filter } = action.payload;
      const groupIndex = state.statFilterGroups.findIndex((group) => group.id === groupId);

      if (groupIndex !== -1) {
        // Don't add if filter with same ID already exists in the group
        if (!state.statFilterGroups[groupIndex].filters.some((f) => f.id === filter.id)) {
          state.statFilterGroups[groupIndex].filters.push(filter);
          saveFiltersToStorage(state);
        }
      }
    },
    removeFilterFromGroup: (
      state,
      action: PayloadAction<{ groupId: string; filterId: string }>
    ) => {
      const { groupId, filterId } = action.payload;
      const groupIndex = state.statFilterGroups.findIndex((group) => group.id === groupId);

      if (groupIndex !== -1) {
        state.statFilterGroups[groupIndex].filters = state.statFilterGroups[
          groupIndex
        ].filters.filter((filter) => filter.id !== filterId);

        // Remove the group if it becomes empty
        if (state.statFilterGroups[groupIndex].filters.length === 0) {
          state.statFilterGroups = state.statFilterGroups.filter((group) => group.id !== groupId);
        }
        saveFiltersToStorage(state);
      }
    },
    updateFilterInGroup: (
      state,
      action: PayloadAction<{ groupId: string; filter: StatFilter }>
    ) => {
      const { groupId, filter } = action.payload;
      const groupIndex = state.statFilterGroups.findIndex((group) => group.id === groupId);

      if (groupIndex !== -1) {
        const filterIndex = state.statFilterGroups[groupIndex].filters.findIndex(
          (f) => f.id === filter.id
        );

        if (filterIndex !== -1) {
          state.statFilterGroups[groupIndex].filters[filterIndex] = filter;
          saveFiltersToStorage(state);
        }
      }
    },

    // New action to trigger applying the search derived from filters
    applyFilterSearch: (state) => {
      state.shouldApplySearch = true;
      saveFiltersToStorage(state);
    },

    // Reset the flag after search is applied
    resetApplySearch: (state) => {
      state.shouldApplySearch = false;
      saveFiltersToStorage(state);
    },

    clearFilterValues: (state) => {
      state.rarity = '';
      state.type = '';
      state.itemType = '';
      state.newItems = '';
      state.ethereal = '';
      state.unidentified = '';
      state.limitFilter = 100;
      state.viewType = 'all';
      state.priceFilter = 'all';
      state.excludeStatFilters = [];
      state.statFilterGroups = [];
      saveFiltersToStorage(state);
    },
  },
});

export const {
  setRarity,
  setType,
  setItemType,
  setNewItems,
  setEthereal,
  setUnidentified,
  setLimitFilter,
  setViewType,
  setPriceFilter,
  toggleFilterBubble,
  setSavedSearches,
  setSavedSearchSent,
  setSearchTerm,
  addStatFilter,
  removeStatFilter,
  updateStatFilter,
  clearStatFilters,
  // New stat filter group actions
  setStatFilterGroups,
  addStatFilterGroup,
  removeStatFilterGroup,
  addFilterToGroup,
  removeFilterFromGroup,
  updateFilterInGroup,
  clearFilterValues,
  // New search application actions
  applyFilterSearch,
  resetApplySearch,
} = filterSlice.actions;

// Selector to check if filters should be applied
export const selectShouldApplySearch = (state: RootState): boolean => {
  return state.filters.shouldApplySearch;
};

export default filterSlice.reducer;
